import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { preventEnterSubmit } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { useAuth } from '@/contexts/AuthContext';
import { Separator } from '@/components/ui/separator';
import { Loader2 } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/hooks/use-toast';
import { captureError } from '@/lib/sentry';

// Separate schema for magic link (email-only) and password login
const magicLinkSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
});

const passwordSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  password: z.string().min(8, 'Password must be at least 8 characters'),
});

type AuthFormValues = z.infer<typeof passwordSchema>;

export function AuthForm() {
  const [isLoading, setIsLoading] = useState(false);
  const [isLinkSent, setIsLinkSent] = useState(false);
  const [isMagicLink, setIsMagicLink] = useState(false);
  const { signIn, signInWithPassword } = useAuth();
  const { toast } = useToast();

  const form = useForm<AuthFormValues>({
    resolver: zodResolver(isMagicLink ? magicLinkSchema : passwordSchema),
    defaultValues: {
      email: '',
      password: '',
    },
    mode: 'onChange', // Enable real-time validation so button enables when form is valid
  });

  // Update form validation when switching between login methods
  useEffect(() => {
    form.reset({
      email: form.getValues('email'),
      password: '',
    });
    form.clearErrors();
  }, [isMagicLink, form]);

  const onSubmit = async (data: AuthFormValues) => {
    console.log('[AuthForm] Form submitted with:', { isMagicLink, email: data.email });
    if (isLoading) return;

    try {
      setIsLoading(true);
      if (isMagicLink) {
        console.log('[AuthForm] Attempting to sign in with magic link:', data.email);
        await signIn(data.email);
        console.log('[AuthForm] Magic link sent successfully');
        setIsLinkSent(true);
      } else {
        console.log('[AuthForm] Attempting to sign in with password');
        await signInWithPassword(data.email, data.password!);
        console.log('[AuthForm] Password sign in successful');
      }
    } catch (error) {
      console.error('[AuthForm] Auth error:', error);
      setIsLinkSent(false);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleAuthMethod = () => {
    setIsMagicLink(!isMagicLink);
    setIsLinkSent(false);
  };

  const handleForgotPassword = async () => {
    const email = form.getValues('email');
    if (!email || !z.string().email().safeParse(email).success) {
      toast({
        title: "Enter your email",
        description: "Please enter a valid email address first.",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth/callback`,
      });

      if (error) throw error;

      toast({
        title: "Reset link sent",
        description: "Check your email for a password reset link.",
      });
    } catch (error) {
      console.error('[AuthForm] Reset password error:', error);
      captureError(error as Error, { feature: 'auth-auth-form-handle-forgot-password' });
      toast({
        title: "Error",
        description: "Failed to send reset link. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader className="space-y-1">
        <CardTitle className="text-2xl font-bold">Welcome back</CardTitle>
        <CardDescription>
          Sign in to your account using {isMagicLink ? 'magic link' : 'your email and password'}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLinkSent ? (
          <div className="space-y-4 text-center">
            <div className="bg-muted p-4 rounded-lg">
              <h3 className="font-semibold mb-2">Check your email</h3>
              <p className="text-sm text-muted-foreground">
                We've sent you a magic link to sign in. Please check your email and click the link to continue.
              </p>
            </div>
            <Button
              variant="outline"
              className="w-full"
              onClick={() => setIsLinkSent(false)}
              disabled={isLoading}
            >
              Send another link
            </Button>
          </div>
        ) : (
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} onKeyDown={isMagicLink ? undefined : preventEnterSubmit} className="space-y-4">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input
                        type="email"
                        placeholder="name@example.com"
                        {...field}
                        autoComplete="email"
                        autoFocus
                        disabled={isLoading}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {!isMagicLink && (
                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Password</FormLabel>
                      <FormControl>
                        <PasswordInput
                          placeholder="Enter your password"
                          {...field}
                          autoComplete="current-password"
                          disabled={isLoading}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              {!isMagicLink && (
                <button
                  type="button"
                  onClick={handleForgotPassword}
                  disabled={isLoading}
                  className="text-sm text-muted-foreground hover:text-primary underline-offset-4 hover:underline"
                >
                  Forgot password?
                </button>
              )}

              <Button 
                type="submit" 
                className="w-full" 
                disabled={isLoading || !form.formState.isValid}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {isMagicLink ? "Sending link..." : "Signing in..."}
                  </>
                ) : (
                  isMagicLink ? "Continue with Magic Link" : "Sign in"
                )}
              </Button>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <Separator className="w-full" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-background px-2 text-muted-foreground">
                    Or continue with
                  </span>
                </div>
              </div>

              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={toggleAuthMethod}
                disabled={isLoading}
              >
                {isMagicLink ? "Use Password" : "Use Magic Link"}
              </Button>
            </form>
          </Form>
        )}
      </CardContent>
    </Card>
  );
}